import { Formik } from "formik";
import { Building2, Check, UserPlus } from "lucide-react";
import { useState } from "react";
import * as yup from "yup";

import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Badge } from "@src/shared/components/ui/badge";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { Spinner } from "@src/shared/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@src/shared/components/ui/table";

import type {
  InviteMemberValues,
  TeamMember,
  TeamSettingsPageProps,
} from "./TeamSettingsPage.types";

const inviteValidationSchema = yup.object<InviteMemberValues>({
  email: yup
    .string()
    .trim()
    .email("Enter a valid email address.")
    .required("Email is required."),
  firstName: yup.string().trim().required("First name is required."),
  lastName: yup.string().trim().required("Last name is required."),
});

function getStatusBadgeVariant(status: TeamMember["status"]) {
  switch (status) {
    case "active":
      return "default";
    case "pending":
      return "secondary";
    case "expired":
      return "destructive";
    default:
      return "secondary";
  }
}

function formatInviteDate(date: Date): string {
  if (!date || Number.isNaN(date.getTime())) {
    return "Recently";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function TeamSettingsPageV2({
  practiceName: initialPracticeName,
  members,
  isLoadingMembers = false,
  membersLoadError = null,
  isSavingPracticeName = false,
  isSendingInvite = false,
  inviteSent = false,
  inviteError = null,
  practiceNameError = null,
  onSavePracticeName,
  onSendInvite,
}: TeamSettingsPageProps) {
  const [practiceName, setPracticeName] = useState(initialPracticeName);

  const handleSavePracticeName = async () => {
    const trimmed = practiceName.trim();
    if (!trimmed) return;
    await onSavePracticeName(trimmed);
  };

  return (
    <VerticalFlexbox className="w-full max-w-[960px] p-6" gap="lg">
      <VerticalFlexbox gap="xs">
        <HorizontalFlexbox alignItems="center" gap="sm">
          <Building2 aria-hidden="true" className="size-5 text-primary" />
          <HeaderText tag="h1" className="text-[22px] tracking-tight">
            Team
          </HeaderText>
        </HorizontalFlexbox>
        <Text appearance="subdued" sizing="sm">
          Manage who can access this practice in Slate.
        </Text>
      </VerticalFlexbox>

      {/* Practice Name Section */}
      <VerticalFlexbox gap="md">
        <VerticalFlexbox gap="xs">
          <Label htmlFor="practice-name">Practice name</Label>
          <HorizontalFlexbox gap="sm">
            <Input
              id="practice-name"
              value={practiceName}
              onChange={(e) => setPracticeName(e.target.value)}
              disabled={isSavingPracticeName}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleSavePracticeName();
                }
              }}
            />
            <Button
              onClick={() => void handleSavePracticeName()}
              disabled={!practiceName.trim() || isSavingPracticeName}
            >
              {isSavingPracticeName ? (
                <Spinner className="mr-1" size="sm" />
              ) : null}
              Update
            </Button>
          </HorizontalFlexbox>
        </VerticalFlexbox>
        <ErrorMessage error={practiceNameError} variant="boxed" />
      </VerticalFlexbox>

      {/* Invite Section */}
      <VerticalFlexbox className="border-t border-border pt-6" gap="md">
        <VerticalFlexbox gap="xs">
          <HorizontalFlexbox alignItems="center" gap="sm">
            <UserPlus aria-hidden="true" className="size-4" />
            <Text className="font-semibold">Add a user to your practice</Text>
          </HorizontalFlexbox>
          <Text appearance="subdued" sizing="sm">
            Invite someone by email. They'll get access once they accept.
          </Text>
        </VerticalFlexbox>

        <Formik<InviteMemberValues>
          initialValues={{ email: "", firstName: "", lastName: "" }}
          validationSchema={inviteValidationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              await onSendInvite(values);
              resetForm();
            } catch {
              // Error is handled by the application layer
            }
          }}
        >
          {({
            handleSubmit,
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
          }) => (
            <VerticalFlexbox as="form" gap="md" onSubmit={handleSubmit}>
              <div className="grid gap-4 md-stitch:grid-cols-2">
                <VerticalFlexbox gap="xs">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSendingInvite}
                    aria-invalid={!!(touched.firstName && errors.firstName)}
                  />
                  {touched.firstName && errors.firstName ? (
                    <Text
                      appearance="subdued"
                      sizing="sm"
                      className="text-destructive"
                    >
                      {errors.firstName}
                    </Text>
                  ) : null}
                </VerticalFlexbox>

                <VerticalFlexbox gap="xs">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSendingInvite}
                    aria-invalid={!!(touched.lastName && errors.lastName)}
                  />
                  {touched.lastName && errors.lastName ? (
                    <Text
                      appearance="subdued"
                      sizing="sm"
                      className="text-destructive"
                    >
                      {errors.lastName}
                    </Text>
                  ) : null}
                </VerticalFlexbox>
              </div>

              <VerticalFlexbox gap="xs">
                <Label htmlFor="email">Email</Label>
                <HorizontalFlexbox gap="sm">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@practice.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSendingInvite}
                    aria-invalid={!!(touched.email && errors.email)}
                  />
                  <Button
                    type="submit"
                    disabled={isSendingInvite || Object.keys(errors).length > 0}
                  >
                    {isSendingInvite ? (
                      <Spinner className="mr-1" size="sm" />
                    ) : null}
                    Send invite
                  </Button>
                </HorizontalFlexbox>
                {touched.email && errors.email ? (
                  <Text
                    appearance="subdued"
                    sizing="sm"
                    className="text-destructive"
                  >
                    {errors.email}
                  </Text>
                ) : null}
              </VerticalFlexbox>

              {inviteSent && !inviteError ? (
                <Alert>
                  <Check className="size-4" />
                  <AlertDescription>Invite sent successfully.</AlertDescription>
                </Alert>
              ) : null}

              <ErrorMessage error={inviteError} variant="boxed" />
            </VerticalFlexbox>
          )}
        </Formik>
      </VerticalFlexbox>

      {/* Team Members Section */}
      <VerticalFlexbox className="border-t border-border pt-6" gap="md">
        <Text className="font-semibold">Your team</Text>

        {membersLoadError ? (
          <ErrorMessage error={membersLoadError} variant="boxed" />
        ) : isLoadingMembers ? (
          <VerticalFlexbox gap="sm">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </VerticalFlexbox>
        ) : members.length === 0 ? (
          <Alert>
            <AlertDescription>
              No team members yet. Send an invite above to get started.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Invited on</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      {member.email}
                    </TableCell>
                    <TableCell>
                      {member.firstName && member.lastName
                        ? `${member.firstName} ${member.lastName}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatInviteDate(member.invitedOn)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(member.status)}>
                        {member.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}
